import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { WEBSITE_HIGHLIGHT } from "../config";
import { useLang } from "../LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

export const Websites = () => {
  const { t, lang } = useLang();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="websites" data-testid="websites" className="relative py-24 md:py-32 bg-[#080808] overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#d4af37]/6 blur-[140px] z-0" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-16">
          <Reveal><Eyebrow testId="websites-eyebrow">{t.websites.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
              {t.websites.title1} <span className="text-gold-gradient italic">{t.websites.title2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#a3a3a3] text-base md:text-lg">{t.websites.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {t.websites.packages.map((p, i) => {
            const highlight = WEBSITE_HIGHLIGHT[i];
            return (
              <motion.div
                key={i}
                data-testid={`website-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                  highlight
                    ? "bg-gradient-to-b from-[#161206] to-[#0b0b0b] border border-[#d4af37]/50 gold-glow lg:-mt-6 lg:mb-6"
                    : "glass"
                }`}
              >
                {highlight && (
                  <span className="absolute top-6 right-6 text-[10px] tracking-[0.25em] uppercase bg-[#d4af37] text-[#050505] px-3 py-1 rounded-full">
                    {lang === "es" ? "Más Popular" : "Most Popular"}
                  </span>
                )}
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37]">{p.name}</div>
                <div className="mt-5">
                  {p.starting && <div className="text-xs text-[#a3a3a3] mb-1">{t.websites.startingAt}</div>}
                  <span className="font-display text-4xl md:text-5xl text-white">{p.price}</span>
                </div>
                <p className="mt-4 text-[#a3a3a3] text-sm md:text-base min-h-[3.5rem]">{p.tagline}</p>
                <div className="divider-gold my-7" />
                <ul className="space-y-3 flex-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#d4d4d4]">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-[#d4af37]" />
                      </span>
                      <span className="text-sm md:text-base">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-col gap-3">
                  <button
                    data-testid={`website-get-started-${i}`}
                    onClick={() => go("booking")}
                    className={`rounded-full px-6 py-3.5 font-medium transition-colors duration-300 ${
                      highlight ? "bg-[#d4af37] text-[#050505] hover:bg-[#e9d08a]" : "bg-white text-[#050505] hover:bg-[#e9d08a]"
                    }`}
                  >
                    {t.websites.getStarted}
                  </button>
                  <button
                    data-testid={`website-contact-${i}`}
                    onClick={() => go("contact")}
                    className="rounded-full px-6 py-3.5 glass text-white hover:bg-white/10 transition-colors duration-300"
                  >
                    {t.websites.contactUs}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="websites-note" className="mt-12 glass rounded-2xl px-8 py-8 flex items-start gap-4 max-w-4xl">
            <Sparkles className="text-[#d4af37] flex-shrink-0 mt-1" size={22} />
            <p className="font-display text-lg md:text-2xl text-white/90 leading-snug italic">{t.websites.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
